import { readImageFileAsDataUrl } from "@/character/frame";
import {
  defaultRoomCellsFromBlob,
  detectImageBlobs,
  type DetectedBlob,
} from "@/home/auto-split";
import {
  mountHomeSheetGridCrop,
  pixelRectForGridCell,
  pixelRectForSheetSelection,
  type HomeSheetGridCropHandle,
} from "@/home/mount-home-sheet-grid-crop";
import { listAllSliceCells } from "@/home/sheet-crop";
import { mountKindPicker } from "@/home/kind-picker";
import { normalizePartKind, type HomePartKind } from "@/home/part-kind";
import { clampPartCells } from "@/home/storage";
import { imageDataUrlForDisplay } from "@/home/image-data-url";
import {
  applyAutoBackgroundRemovalFromDataUrl,
  bindAutoRemoveBgCheckbox,
  homeAutoRemoveBgToggleMarkup,
  isAutoRemoveBlackBgEnabled,
  isAutoRemoveWhiteBgEnabled,
} from "@/home/remove-white-bg";
import { cropImageToDataUrl, type PixelRect } from "@/home/sheet-crop";

const AUTO_SPLIT_MIN_AREA = 800;
const AUTO_SPLIT_PADDING = 8;
const AUTO_SPLIT_DEFAULT_KIND: HomePartKind = "solid";

export type HomeSheetUploadPreset = "default" | "floor_tile" | "shell";

export type HomeSheetCropOptions = {
  /** Limit kind picker per piece (default: all picker kinds). */
  allowedKinds?: HomePartKind[];
  defaultAutoSplitKind?: HomePartKind;
  /** Primary button on review step (default: สร้างชิ้นที่เลือก). */
  createButtonLabel?: string;
  uploadPreset?: HomeSheetUploadPreset;
};

function allowedKindsForPreset(
  preset: HomeSheetUploadPreset,
  base?: HomePartKind[],
): HomePartKind[] | undefined {
  if (preset === "floor_tile") return ["floor"];
  if (preset === "shell") return ["shell"];
  return base;
}

function defaultKindForPreset(preset: HomeSheetUploadPreset, base: HomePartKind): HomePartKind {
  if (preset === "floor_tile") return "floor";
  if (preset === "shell") return "shell";
  return base;
}

function resolveDraftKind(
  kind: HomePartKind,
  allowedKinds: HomePartKind[] | undefined,
  fallback: HomePartKind,
): HomePartKind {
  const normalized = normalizePartKind(kind);
  if (!allowedKinds?.length) return normalized;
  return allowedKinds.includes(normalized) ? normalized : fallback;
}

type SheetWizardStep = "upload" | "crop" | "review";

const STEP_LABELS: Record<SheetWizardStep, string> = {
  upload: "เลือกรูป",
  crop: "ครอปกริด",
  review: "ตั้งค่า",
};

export type HomePartDraft = {
  name: string;
  imageDataUrl: string;
  cellsW: number;
  cellsH: number;
  kind?: HomePartKind;
  tags?: string[];
};

export type HomeSheetCropHandle = {
  destroy: () => void;
  loadSourceImage: (
    dataUrl: string,
    fileLabel: string,
    opts?: { autoSplit?: boolean; startStep?: "upload" | "crop" },
  ) => Promise<void>;
  resetWizard: () => void;
  setUploadPreset: (preset: HomeSheetUploadPreset) => void;
  getUploadPreset: () => HomeSheetUploadPreset;
};

export function mountHomeSheetCrop(
  host: HTMLElement,
  onCreateParts: (parts: HomePartDraft[]) => void,
  options?: HomeSheetCropOptions,
): HomeSheetCropHandle {
  const baseAllowedKinds = options?.allowedKinds;
  const baseDefaultAutoSplitKind =
    options?.defaultAutoSplitKind ?? baseAllowedKinds?.[0] ?? AUTO_SPLIT_DEFAULT_KIND;
  const reviewCreateLabel = options?.createButtonLabel ?? "สร้างชิ้นที่เลือก";
  let uploadPreset: HomeSheetUploadPreset = options?.uploadPreset ?? "default";

  const effectiveAllowedKinds = (): HomePartKind[] | undefined =>
    allowedKindsForPreset(uploadPreset, baseAllowedKinds);

  const effectiveDefaultAutoSplitKind = (): HomePartKind =>
    defaultKindForPreset(uploadPreset, baseDefaultAutoSplitKind);
  let sourceImage: HTMLImageElement | null = null;
  let sourceOriginalUrl: string | null = null;
  let sourcePreviewUrl: string | null = null;
  let sourceFileLabel = "";
  let sheetStep: SheetWizardStep = "upload";
  let autoRows: Array<{
    blob: DetectedBlob;
    selected: boolean;
    name: string;
    kind: HomePartKind;
    cellsW: number;
    cellsH: number;
  }> = [];

  host.innerHTML = `
    <div class="home-sheet-wizard">
      <nav class="home-sheet-quest-bar" aria-label="ขั้นตอน">
        <span class="home-sheet-quest-step" data-quest-step="upload">① รูป</span>
        <span class="home-sheet-quest-connector" aria-hidden="true"></span>
        <span class="home-sheet-quest-step" data-quest-step="crop">② ครอป</span>
        <span class="home-sheet-quest-connector" aria-hidden="true"></span>
        <span class="home-sheet-quest-step" data-quest-step="review">③ คลัง</span>
      </nav>

      <section id="sheet-step-upload" class="home-sheet-step home-sheet-step--upload" data-sheet-step="upload">
        <div class="home-sheet-hero-card home-sheet-hero-card--upload">
          <header class="home-sheet-upload-head">
            <p class="home-sheet-hero-kicker">ขั้นที่ 1</p>
            <h3 id="sheet-upload-hero-title" class="home-sheet-hero-title">อัปโหลดภาพแผ่น</h3>
            <p id="sheet-upload-hero-hint" class="hint home-sheet-hero-hint">PNG โปร่งใส · แยกชิ้นอัตโนมัติ</p>
          </header>
          <div class="home-sheet-upload-stack">
            ${homeAutoRemoveBgToggleMarkup({
              id: "sheet-auto-remove-bg",
              extraClass: "home-sheet-bg-auto",
            })}
            <label class="btn btn-primary home-sheet-upload-cta sheet-upload">
              เลือกรูปจากเครื่อง
              <input type="file" id="sheet-file" accept="image/png,image/jpeg,image/webp" hidden />
            </label>
          </div>
          <div id="sheet-upload-preview-wrap" class="home-sheet-upload-preview-wrap" hidden>
            <img id="sheet-upload-preview" class="home-sheet-upload-preview" alt="" />
            <p id="sheet-file-name" class="hint sheet-file-meta"></p>
          </div>
          <p id="sheet-upload-busy" class="hint home-sheet-split-status" hidden>กำลังแยกชิ้นจากภาพ…</p>
        </div>
      </section>

      <section id="sheet-step-crop" class="home-sheet-step home-sheet-step--crop" data-sheet-step="crop" hidden>
        <div id="sheet-grid-crop-host"></div>
        <div class="home-sheet-crop-actions home-sheet-crop-actions--game-row" role="group" aria-label="เพิ่มชิ้นจากกริด">
          <button type="button" id="sheet-grid-add-piece" class="home-sheet-crop-game-btn home-sheet-crop-game-btn--primary" hidden disabled>เพิ่ม</button>
          <button type="button" id="sheet-grid-split-all" class="home-sheet-crop-game-btn" disabled>ทุกช่อง</button>
          <button type="button" id="sheet-grid-auto-split" class="home-sheet-crop-game-btn" disabled>อัตโนมัติ</button>
        </div>
        <p id="sheet-crop-queue-status" class="hint home-sheet-crop-queue">คิว 0</p>
      </section>

      <section id="sheet-step-review" class="home-sheet-step home-sheet-step--review" data-sheet-step="review" hidden>
        <header class="home-sheet-items-head">
          <div class="home-sheet-items-head-row">
            <div>
              <p class="home-sheet-hero-kicker">ขั้นที่ 2</p>
              <h3 class="home-sheet-items-title">ตั้งค่าแต่ละชิ้น</h3>
            </div>
            <button type="button" id="auto-split-select-all" class="btn btn-ghost btn-sm home-sheet-select-all" disabled hidden>
              เลือกทั้งหมด
            </button>
          </div>
          <p id="auto-split-status" class="hint home-sheet-items-status">—</p>
        </header>
        <div class="auto-split-scroll-shell" id="auto-split-scroll-shell" hidden>
          <div id="auto-split-list" class="auto-split-list auto-split-list--panel" role="list"></div>
        </div>
      </section>

      <p id="sheet-crop-error" class="form-error home-sheet-wizard-error" role="alert" hidden></p>

      <footer class="home-sheet-wizard-foot">
        <button type="button" id="sheet-step-back" class="btn btn-ghost home-sheet-foot-back" hidden>
          กลับ
        </button>
        <span class="home-sheet-foot-spacer"></span>
        <button type="button" id="sheet-step-primary" class="btn btn-primary home-sheet-foot-primary" disabled>
          ถัดไป
        </button>
      </footer>
    </div>
  `;

  const fileNameEl = host.querySelector<HTMLElement>("#sheet-file-name");
  const uploadBusyEl = host.querySelector<HTMLElement>("#sheet-upload-busy");
  const errorEl = host.querySelector<HTMLElement>("#sheet-crop-error");
  const autoSplitStatus = host.querySelector<HTMLElement>("#auto-split-status");
  const autoSplitScrollShell = host.querySelector<HTMLElement>("#auto-split-scroll-shell");
  const autoSplitList = host.querySelector<HTMLElement>("#auto-split-list");
  const autoSplitSelectAllBtn = host.querySelector<HTMLButtonElement>("#auto-split-select-all");
  const stepBackBtn = host.querySelector<HTMLButtonElement>("#sheet-step-back");
  const stepPrimaryBtn = host.querySelector<HTMLButtonElement>("#sheet-step-primary");
  const uploadPreviewWrap = host.querySelector<HTMLElement>("#sheet-upload-preview-wrap");
  const uploadPreviewImg = host.querySelector<HTMLImageElement>("#sheet-upload-preview");
  const uploadHeroTitle = host.querySelector<HTMLElement>("#sheet-upload-hero-title");
  const uploadHeroHint = host.querySelector<HTMLElement>("#sheet-upload-hero-hint");
  const gridCropHost = host.querySelector<HTMLElement>("#sheet-grid-crop-host");
  const gridAddPieceBtn = host.querySelector<HTMLButtonElement>("#sheet-grid-add-piece");
  const gridSplitAllBtn = host.querySelector<HTMLButtonElement>("#sheet-grid-split-all");
  const gridAutoSplitBtn = host.querySelector<HTMLButtonElement>("#sheet-grid-auto-split");
  const cropQueueStatus = host.querySelector<HTMLElement>("#sheet-crop-queue-status");

  const commitGridPieceFromSelection = () => {
    if (!sourceImage || !gridCrop) return;
    hideError();
    const rect = exportRectFromGridCrop();
    if (!rect) return;
    const blob = detectedBlobFromRect(rect);
    const prefix = defaultNamePrefix();
    pushBlobAsRow(
      blob,
      `${prefix} ${String(autoRows.length + 1).padStart(2, "0")}`,
      gridCellsFromCrop(),
    );
    gridCrop.clearSelection();
    syncCropStepControls();
  };

  let gridCrop: HomeSheetGridCropHandle | undefined;
  if (gridCropHost) {
    gridCrop = mountHomeSheetGridCrop(gridCropHost, {
      onInteractionChange: () => syncCropStepControls(),
      onGridSelectionCommit: () => commitGridPieceFromSelection(),
    });
  }

  const detectedBlobFromRect = (rect: {
    sx: number;
    sy: number;
    sw: number;
    sh: number;
  }): DetectedBlob => {
    if (!sourceImage) {
      throw new Error("ไม่มีภาพต้นฉบับ");
    }
    const normalized = {
      sx: Math.round(rect.sx),
      sy: Math.round(rect.sy),
      sw: Math.max(1, Math.round(rect.sw)),
      sh: Math.max(1, Math.round(rect.sh)),
    };
    return {
      id: `grid-${normalized.sx}-${normalized.sy}-${normalized.sw}x${normalized.sh}`,
      ...normalized,
      area: normalized.sw * normalized.sh,
      previewDataUrl: cropImageToDataUrl(sourceImage, normalized, { maxEdge: 160 }),
    };
  };

  const syncCropQueueStatus = () => {
    if (!cropQueueStatus) return;
    const n = autoRows.length;
    cropQueueStatus.textContent = n > 0 ? `คิว ${n} ชิ้น` : "คิว 0";
  };

  const syncCropStepControls = () => {
    const hasImage = Boolean(sourceImage);
    if (gridAddPieceBtn) {
      gridAddPieceBtn.disabled = !hasImage || !gridCrop?.getFrameRect();
    }
    if (gridSplitAllBtn) gridSplitAllBtn.disabled = !hasImage;
    if (gridAutoSplitBtn) gridAutoSplitBtn.disabled = !hasImage;
    gridCrop?.repaint();
    syncCropQueueStatus();
    syncPrimaryButton();
  };

  const guessSheetGridFromImage = (img: HTMLImageElement): { cols: number; rows: number } => {
    const aspect = img.naturalWidth / Math.max(1, img.naturalHeight);
    const maxSide = Math.max(img.naturalWidth, img.naturalHeight);
    if (maxSide <= 128) return { cols: 1, rows: 1 };
    let cols = 4;
    let rows = 4;
    if (aspect >= 1.6) cols = 6;
    else if (aspect >= 1.1) cols = 5;
    else if (aspect <= 0.65) rows = 6;
    else if (aspect <= 0.85) rows = 5;
    return { cols, rows };
  };

  const exportRectFromGridCrop = (): PixelRect | null => {
    if (!sourceImage || !gridCrop) return null;
    const region = gridCrop.getFrameRect();
    if (!region) return null;
    const grid = gridCrop.getGrid();
    const sel = gridCrop.getSelection();
    if (grid.cols === 1 && grid.rows === 1) {
      return { ...region };
    }
    if (!sel) return null;
    return pixelRectForSheetSelection(region, grid, sel);
  };

  const enterCropStep = () => {
    if (sourceImage) {
      gridCrop?.setImage(sourceImage);
      gridCrop?.setGrid(guessSheetGridFromImage(sourceImage));
    }
    syncCropStepControls();
    goToStep("crop");
  };

  const gridCellsFromCrop = (): { cellsW: number; cellsH: number } => {
    if (!gridCrop) return { cellsW: 1, cellsH: 1 };
    const grid = gridCrop.getGrid();
    const sel = gridCrop.getSelection();
    if (grid.cols === 1 && grid.rows === 1) {
      return { cellsW: 1, cellsH: 1 };
    }
    if (sel) {
      return {
        cellsW: clampPartCells(sel.cellsW),
        cellsH: clampPartCells(sel.cellsH),
      };
    }
    return { cellsW: 1, cellsH: 1 };
  };

  const pushBlobAsRow = (
    blob: DetectedBlob,
    name: string,
    gridCells?: { cellsW: number; cellsH: number },
  ) => {
    const floorTile = uploadPreset === "floor_tile";
    let cellsW = 1;
    let cellsH = 1;
    if (gridCells) {
      cellsW = clampPartCells(gridCells.cellsW);
      cellsH = clampPartCells(gridCells.cellsH);
    } else {
      const size = defaultRoomCellsFromBlob(blob, !floorTile, 1, 1);
      cellsW = clampPartCells(size.cellsW);
      cellsH = clampPartCells(size.cellsH);
    }
    autoRows.push({
      blob,
      selected: true,
      name,
      kind: effectiveDefaultAutoSplitKind(),
      cellsW,
      cellsH,
    });
    syncCropQueueStatus();
    syncPrimaryButton();
  };

  const dispatchPresetChange = () => {
    host.dispatchEvent(
      new CustomEvent("home-sheet-upload-preset", {
        bubbles: true,
        detail: { preset: uploadPreset },
      }),
    );
  };

  const syncUploadPresetCopy = () => {
    if (uploadPreset === "floor_tile") {
      if (uploadHeroTitle) uploadHeroTitle.textContent = "อัปโหลดกระเบื้องพื้น";
      if (uploadHeroHint) {
        uploadHeroHint.textContent =
          "รูปสี่เหลี่ยมจัตุรัส · 1 ช่องกริด · วางแล้วอยู่ชั้นหลัง (ใต้บ้านและของตกแต่ง)";
      }
    } else {
      if (uploadHeroTitle) uploadHeroTitle.textContent = "อัปโหลดภาพแผ่น";
      if (uploadHeroHint) uploadHeroHint.textContent = "PNG โปร่งใส · แยกชิ้นอัตโนมัติ";
    }
    dispatchPresetChange();
  };

  const setUploadPreset = (preset: HomeSheetUploadPreset) => {
    uploadPreset = preset;
    syncUploadPresetCopy();
  };

  const dispatchStepChange = () => {
    host.dispatchEvent(
      new CustomEvent("home-sheet-step", {
        bubbles: true,
        detail: { step: sheetStep, label: STEP_LABELS[sheetStep] },
      }),
    );
  };

  const syncQuestBar = () => {
    const order: SheetWizardStep[] = ["upload", "crop", "review"];
    const activeIndex = order.indexOf(sheetStep);
    host.querySelectorAll<HTMLElement>("[data-quest-step]").forEach((el) => {
      const step = el.dataset.questStep as SheetWizardStep | undefined;
      if (!step) return;
      const idx = order.indexOf(step);
      el.classList.toggle("home-sheet-quest-step--active", step === sheetStep);
      el.classList.toggle("home-sheet-quest-step--done", idx >= 0 && idx < activeIndex);
    });
  };

  const syncSheetSteps = () => {
    host.querySelectorAll<HTMLElement>("[data-sheet-step]").forEach((section) => {
      const step = section.dataset.sheetStep as SheetWizardStep | undefined;
      section.hidden = step !== sheetStep;
    });
    if (stepBackBtn) stepBackBtn.hidden = sheetStep === "upload";
    syncQuestBar();
    syncPrimaryButton();
    dispatchStepChange();
    if (sheetStep === "crop") syncCropStepControls();
  };

  const syncPrimaryButton = () => {
    if (!stepPrimaryBtn) return;
    stepPrimaryBtn.hidden = false;
    if (sheetStep === "upload") {
      const busy = Boolean(uploadBusyEl && !uploadBusyEl.hidden);
      stepPrimaryBtn.textContent = busy ? "กำลังโหลด…" : "ถัดไป";
      stepPrimaryBtn.disabled = !sourceImage || busy;
      return;
    }
    if (sheetStep === "crop") {
      stepPrimaryBtn.textContent = autoRows.length > 0 ? `ถัดไป (${autoRows.length})` : "ถัดไป";
      stepPrimaryBtn.disabled = autoRows.length === 0;
      return;
    }
    const selectedCount = autoRows.filter((r) => r.selected).length;
    stepPrimaryBtn.textContent =
      selectedCount > 0 ? `${reviewCreateLabel} (${selectedCount})` : reviewCreateLabel;
    stepPrimaryBtn.disabled = selectedCount === 0;
  };

  const syncAutoSplitButtons = () => {
    const allSelected = autoRows.length > 0 && autoRows.every((r) => r.selected);
    if (autoSplitSelectAllBtn) {
      autoSplitSelectAllBtn.hidden = sheetStep !== "review" || autoRows.length === 0;
      autoSplitSelectAllBtn.disabled = autoRows.length === 0;
      autoSplitSelectAllBtn.textContent = allSelected ? "ยกเลิกเลือก" : "เลือกทั้งหมด";
    }
    syncPrimaryButton();
  };

  const updateSourceMeta = () => {
    const label = sourceFileLabel || "ยังไม่ได้เลือกรูป";
    const dims =
      sourceImage != null
        ? `${sourceImage.naturalWidth}×${sourceImage.naturalHeight}`
        : "";
    const meta = dims ? `${label} · ${dims}` : label;
    if (fileNameEl) fileNameEl.textContent = meta;
  };

  const syncUploadPreview = () => {
    const has = Boolean(sourceImage && sourcePreviewUrl);
    if (uploadPreviewWrap) uploadPreviewWrap.hidden = !has;
    if (uploadPreviewImg && sourcePreviewUrl) {
      uploadPreviewImg.src = sourcePreviewUrl;
    } else if (uploadPreviewImg) {
      uploadPreviewImg.removeAttribute("src");
    }
    updateSourceMeta();
    syncPrimaryButton();
  };

  const renderAutoSplitList = () => {
    if (!autoSplitList) return;
    autoSplitList.replaceChildren();
    const hasRows = autoRows.length > 0;
    autoSplitList.hidden = !hasRows;
    if (autoSplitScrollShell) autoSplitScrollShell.hidden = !hasRows;
    if (!hasRows) {
      syncAutoSplitButtons();
      return;
    }

    for (let index = 0; index < autoRows.length; index += 1) {
      const row = autoRows[index]!;
      const article = document.createElement("article");
      article.className = "auto-split-piece";
      article.setAttribute("role", "listitem");
      if (row.selected) article.classList.add("auto-split-piece--on");

      const setSelected = (selected: boolean) => {
        const r = autoRows[index];
        if (!r) return;
        r.selected = selected;
        check.checked = selected;
        article.classList.toggle("auto-split-piece--on", selected);
        syncAutoSplitButtons();
      };

      const top = document.createElement("div");
      top.className = "auto-split-piece-top";

      const checkLabel = document.createElement("label");
      checkLabel.className = "auto-split-piece-pick";
      const check = document.createElement("input");
      check.type = "checkbox";
      check.className = "auto-split-selected";
      check.checked = row.selected;
      check.addEventListener("change", () => setSelected(check.checked));
      const pickText = document.createElement("span");
      pickText.className = "auto-split-piece-pick-label";
      pickText.textContent = "เลือก";
      checkLabel.append(check, pickText);

      const meta = document.createElement("span");
      meta.className = "auto-split-piece-px";
      meta.textContent = `${Math.round(row.blob.sw)}×${Math.round(row.blob.sh)}px`;
      meta.title = `${Math.round(row.blob.sw)}×${Math.round(row.blob.sh)} พิกเซล`;
      top.append(checkLabel, meta);

      const preview = document.createElement("div");
      preview.className = "auto-split-piece-preview";
      preview.title = "แตะเพื่อเลือก/ยกเลิก";
      const thumb = document.createElement("img");
      thumb.className = "auto-split-piece-thumb";
      thumb.alt = row.name;
      thumb.src = imageDataUrlForDisplay(row.blob.previewDataUrl);
      preview.append(thumb);
      preview.addEventListener("click", () => setSelected(!autoRows[index]?.selected));

      const body = document.createElement("div");
      body.className = "auto-split-piece-body";

      const nameField = document.createElement("label");
      nameField.className = "auto-split-piece-field";
      const nameLabel = document.createElement("span");
      nameLabel.className = "auto-split-piece-field-label";
      nameLabel.textContent = "ชื่อ";
      const nameInput = document.createElement("input");
      nameInput.type = "text";
      nameInput.className = "text-input auto-split-piece-input auto-split-name";
      nameInput.maxLength = 24;
      nameInput.value = row.name;
      nameInput.title = row.name;
      nameInput.placeholder = "ตั้งชื่อชิ้น";
      nameInput.addEventListener("input", () => {
        const r = autoRows[index];
        if (!r) return;
        r.name = nameInput.value;
        nameInput.title = nameInput.value;
      });
      nameField.append(nameLabel, nameInput);

      const kindField = document.createElement("div");
      kindField.className = "auto-split-piece-field auto-split-piece-kind";
      const kindLabel = document.createElement("span");
      kindLabel.className = "auto-split-piece-field-label";
      kindLabel.textContent = "หมวด";
      const kindPicker = mountKindPicker(row.kind, {
        compact: true,
        kinds: effectiveAllowedKinds(),
        variant: "select",
        showHint: false,
      });
      kindPicker.root.addEventListener("home-kind-change", () => {
        const r = autoRows[index];
        if (!r) return;
        r.kind = kindPicker.getValue();
      });
      kindField.append(kindLabel, kindPicker.root);

      const sizeRow = document.createElement("div");
      sizeRow.className = "auto-split-piece-size";
      sizeRow.setAttribute("role", "group");
      sizeRow.setAttribute("aria-label", "ขนาดบนกริด");

      const wField = document.createElement("label");
      wField.className = "auto-split-piece-size-cell";
      const wLabel = document.createElement("span");
      wLabel.className = "auto-split-piece-field-label";
      wLabel.textContent = "กว้าง";
      const wInput = document.createElement("input");
      wInput.type = "number";
      wInput.className = "text-input auto-split-piece-input auto-split-piece-num auto-split-cells-w";
      wInput.min = "1";
      wInput.max = "4";
      wInput.inputMode = "numeric";
      wInput.value = String(row.cellsW);
      wInput.addEventListener("change", () => {
        const r = autoRows[index];
        if (!r) return;
        r.cellsW = clampPartCells(Number(wInput.value));
        wInput.value = String(r.cellsW);
      });
      wField.append(wLabel, wInput);

      const sizeMid = document.createElement("span");
      sizeMid.className = "auto-split-piece-size-x";
      sizeMid.textContent = "×";
      sizeMid.setAttribute("aria-hidden", "true");

      const hField = document.createElement("label");
      hField.className = "auto-split-piece-size-cell";
      const hLabel = document.createElement("span");
      hLabel.className = "auto-split-piece-field-label";
      hLabel.textContent = "สูง";
      const hInput = document.createElement("input");
      hInput.type = "number";
      hInput.className = "text-input auto-split-piece-input auto-split-piece-num auto-split-cells-h";
      hInput.min = "1";
      hInput.max = "4";
      hInput.inputMode = "numeric";
      hInput.value = String(row.cellsH);
      hInput.addEventListener("change", () => {
        const r = autoRows[index];
        if (!r) return;
        r.cellsH = clampPartCells(Number(hInput.value));
        hInput.value = String(r.cellsH);
      });
      hField.append(hLabel, hInput);

      const sizeHint = document.createElement("span");
      sizeHint.className = "auto-split-piece-size-hint";
      sizeHint.textContent = "ช่อง";

      sizeRow.append(wField, sizeMid, hField, sizeHint);
      body.append(nameField, kindField, sizeRow);

      article.append(top, preview, body);
      autoSplitList.append(article);
    }

    syncAutoSplitButtons();
  };

  const defaultNamePrefix = (): string => {
    const base = sourceFileLabel.replace(/\.[^.]+$/, "").trim();
    if (!base) return "ชิ้น";
    if (/^[0-9a-f-]{8,}$/i.test(base)) return "ชิ้น";
    return base.slice(0, 20);
  };

  const readAutoSplitOptions = () => ({
    minArea: AUTO_SPLIT_MIN_AREA,
    padding: AUTO_SPLIT_PADDING,
    minAlpha: 12,
    removeWhiteBg: isAutoRemoveWhiteBgEnabled(),
    removeBlackBg: isAutoRemoveBlackBgEnabled(),
  });

  const loadImageFromUrl = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
      img.src = dataUrl;
    });

  const refreshSourceFromOriginal = async () => {
    if (!sourceOriginalUrl) return;
    const url = await applyAutoBackgroundRemovalFromDataUrl(sourceOriginalUrl);
    sourcePreviewUrl = url;
    sourceImage = await loadImageFromUrl(url);
    syncUploadPreview();
  };

  const ingestOriginalDataUrl = async (dataUrl: string, fileLabel: string) => {
    sourceOriginalUrl = dataUrl;
    sourceFileLabel = fileLabel;
    autoRows = [];
    renderAutoSplitList();
    await refreshSourceFromOriginal();
    if (sheetStep === "crop") {
      if (sourceImage) gridCrop?.setImage(sourceImage);
      syncCropStepControls();
    }
  };

  const showError = (message: string) => {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  };

  const hideError = () => {
    errorEl?.setAttribute("hidden", "true");
  };

  const goToStep = (step: SheetWizardStep) => {
    sheetStep = step;
    hideError();
    syncSheetSteps();
  };

  const resetWizard = () => {
    sourceImage = null;
    sourceOriginalUrl = null;
    sourcePreviewUrl = null;
    sourceFileLabel = "";
    autoRows = [];
    sheetStep = "upload";
    hideError();
    renderAutoSplitList();
    syncUploadPreview();
    syncCropQueueStatus();
    if (autoSplitStatus) autoSplitStatus.textContent = "—";
    const fileInput = host.querySelector<HTMLInputElement>("#sheet-file");
    if (fileInput) fileInput.value = "";
    setUploadBusy(false);
    syncSheetSteps();
  };

  const setUploadBusy = (busy: boolean) => {
    if (uploadBusyEl) uploadBusyEl.hidden = !busy;
    syncPrimaryButton();
  };

  const runAutoSplit = async () => {
    if (!sourceImage || !stepPrimaryBtn) return;
    hideError();
    setUploadBusy(true);
    stepPrimaryBtn.disabled = true;

    try {
      const prefix = defaultNamePrefix();
      const defaultKind = effectiveDefaultAutoSplitKind();
      const floorTile = uploadPreset === "floor_tile";
      const blobs = await detectImageBlobs(sourceImage, readAutoSplitOptions());
      autoRows = blobs.map((blob, i) => {
        const size = defaultRoomCellsFromBlob(blob, !floorTile, 1, 1);
        return {
          blob,
          selected: true,
          name: `${prefix} ${String(i + 1).padStart(2, "0")}`,
          kind: defaultKind,
          cellsW: clampPartCells(size.cellsW),
          cellsH: clampPartCells(size.cellsH),
        };
      });
      if (autoSplitStatus) {
        autoSplitStatus.textContent =
          blobs.length > 0
            ? `พบ ${blobs.length} ชิ้น · ตั้งค่าแล้วกดสร้าง`
            : "ไม่พบชิ้นในภาพ";
      }
      if (blobs.length === 0) {
        showError("ไม่พบชิ้น — ใช้ PNG โปร่งใสหรือลองภาพอื่น");
        setUploadBusy(false);
        return;
      }
      renderAutoSplitList();
      setUploadBusy(false);
      goToStep("review");
    } catch (err) {
      autoRows = [];
      renderAutoSplitList();
      if (autoSplitStatus) autoSplitStatus.textContent = "แยกไม่สำเร็จ";
      showError(err instanceof Error ? err.message : "แยกชิ้นไม่สำเร็จ");
      setUploadBusy(false);
    } finally {
      syncAutoSplitButtons();
    }
  };

  const createFromAutoSplit = () => {
    hideError();
    if (!sourceImage) {
      showError("อัปโหลดภาพแผ่นก่อน");
      return;
    }

    const parts: HomePartDraft[] = [];
    for (const row of autoRows) {
      if (!row.selected) continue;
      const name = row.name.trim();
      if (!name) {
        showError("ทุกชิ้นที่เลือกต้องมีชื่อ");
        return;
      }
      const rect = {
        sx: row.blob.sx,
        sy: row.blob.sy,
        sw: row.blob.sw,
        sh: row.blob.sh,
      };
      parts.push({
        name,
        imageDataUrl: cropImageToDataUrl(sourceImage, rect, {
          removeWhiteBg: isAutoRemoveWhiteBgEnabled(),
          removeBlackBg: isAutoRemoveBlackBgEnabled(),
        }),
        cellsW: clampPartCells(row.cellsW),
        cellsH: clampPartCells(row.cellsH),
        kind: resolveDraftKind(row.kind, effectiveAllowedKinds(), effectiveDefaultAutoSplitKind()),
      });
    }
    if (parts.length === 0) {
      showError("เลือกอย่างน้อย 1 ชิ้น");
      return;
    }
    onCreateParts(parts);
    resetWizard();
  };

  const loadSourceImage = async (
    dataUrl: string,
    fileLabel: string,
    opts?: { autoSplit?: boolean; startStep?: "upload" | "crop" },
  ): Promise<void> => {
    hideError();
    await ingestOriginalDataUrl(dataUrl, fileLabel);
    if (opts?.autoSplit) {
      await runAutoSplit();
      return;
    }
    if (opts?.startStep === "crop") {
      enterCropStep();
      return;
    }
    goToStep("upload");
  };

  const autoRemoveBgInput = host.querySelector<HTMLInputElement>("#sheet-auto-remove-bg");
  if (autoRemoveBgInput) bindAutoRemoveBgCheckbox(autoRemoveBgInput);

  const onRemoveBgPrefChanged = () => {
    void (async () => {
      if (!sourceOriginalUrl) return;
      try {
        hideError();
        await refreshSourceFromOriginal();
        if (sheetStep === "review" && sourceImage) {
          await runAutoSplit();
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : "ประมวลผลภาพไม่สำเร็จ");
      }
    })();
  };
  window.addEventListener("home-remove-white-bg-changed", onRemoveBgPrefChanged);
  window.addEventListener("home-remove-black-bg-changed", onRemoveBgPrefChanged);

  host.querySelector<HTMLInputElement>("#sheet-file")?.addEventListener("change", () => {
    const input = host.querySelector<HTMLInputElement>("#sheet-file");
    const file = input?.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        const dataUrl = await readImageFileAsDataUrl(file);
        await ingestOriginalDataUrl(dataUrl, file.name);
        hideError();
      } catch (err) {
        sourceImage = null;
        sourceOriginalUrl = null;
        sourcePreviewUrl = null;
        sourceFileLabel = "";
        syncUploadPreview();
        showError(err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ");
        if (input) input.value = "";
        syncPrimaryButton();
      }
    })();
  });

  stepBackBtn?.addEventListener("click", () => {
    if (sheetStep === "review") enterCropStep();
    else if (sheetStep === "crop") goToStep("upload");
  });

  stepPrimaryBtn?.addEventListener("click", () => {
    if (sheetStep === "upload") {
      if (sourceImage) enterCropStep();
      return;
    }
    if (sheetStep === "crop") {
      if (autoRows.length === 0) {
        showError("เพิ่มชิ้นจากกริดก่อน หรือกดแยกทุกช่อง / แยกอัตโนมัติ");
        return;
      }
      if (autoSplitStatus) {
        autoSplitStatus.textContent = `คิว ${autoRows.length} ชิ้น · ตั้งค่าแล้วกดสร้าง`;
      }
      renderAutoSplitList();
      goToStep("review");
      return;
    }
    createFromAutoSplit();
  });

  gridAddPieceBtn?.addEventListener("click", () => {
    commitGridPieceFromSelection();
  });

  gridSplitAllBtn?.addEventListener("click", () => {
    if (!sourceImage || !gridCrop) return;
    hideError();
    const region = gridCrop.getFrameRect();
    if (!region) return;
    const grid = gridCrop.getGrid();
    const prefix = defaultNamePrefix();
    autoRows = [];
    for (const { col, row } of listAllSliceCells(grid)) {
      const rect = pixelRectForGridCell(region, grid, col, row);
      pushBlobAsRow(
        detectedBlobFromRect(rect),
        `${prefix} ${String(col + 1).padStart(2, "0")}-${String(row + 1).padStart(2, "0")}`,
        { cellsW: 1, cellsH: 1 },
      );
    }
    if (autoSplitStatus) {
      autoSplitStatus.textContent = `พบ ${autoRows.length} ช่องกริด · ตั้งค่าแล้วกดสร้าง`;
    }
    renderAutoSplitList();
    goToStep("review");
  });

  gridAutoSplitBtn?.addEventListener("click", () => {
    if (!sourceImage) return;
    void runAutoSplit();
  });

  autoSplitSelectAllBtn?.addEventListener("click", () => {
    if (autoRows.length === 0) return;
    const allSelected = autoRows.every((r) => r.selected);
    for (const row of autoRows) {
      row.selected = !allSelected;
    }
    renderAutoSplitList();
  });

  syncUploadPresetCopy();
  resetWizard();

  return {
    destroy: () => {
      window.removeEventListener("home-remove-white-bg-changed", onRemoveBgPrefChanged);
      window.removeEventListener("home-remove-black-bg-changed", onRemoveBgPrefChanged);
      gridCrop?.destroy();
      host.replaceChildren();
    },
    loadSourceImage,
    resetWizard,
    setUploadPreset,
    getUploadPreset: () => uploadPreset,
  };
}
